<template>
  <div id="SectionGHGDemo">
    <UIContainer class="SectionGHGDemo__container">
      <div class="SectionGHGDemo__title-container">
        <SectionTitle titleSize="sm">
          <template #title> See how it works in practice </template>
          <template #description>
            3 Minute product demo - Manufacturing case study
          </template>
        </SectionTitle>
      </div>

      <div class="SectionGHGDemo__video-container">
        <div class="SectionGHGDemo__video">
          <div ref="playerEl" class="SectionGHGDemo__player"></div>
        </div>
        <!-- Left overlay badges -->
        <div
          :class="[
            'SectionGHGDemo__left-cards',
            { 'SectionGHGDemo__cards--hidden': isPlaying },
          ]"
        >
          <div class="SectionGHGDemo__badge">
            <div class="SectionGHGDemo__badge-icon">
              <UIIcon name="fileOutline" />
            </div>
            <div class="SectionGHGDemo__badge-text">
              Copy: encryption at rest/in transit
            </div>
          </div>
          <div class="SectionGHGDemo__badge">
            <div class="SectionGHGDemo__badge-icon">
              <UIIcon name="accountGroupOutline" />
            </div>
            <div class="SectionGHGDemo__badge-text">Role-based access</div>
          </div>
          <div class="SectionGHGDemo__badge">
            <div class="SectionGHGDemo__badge-icon">
              <UIIcon name="checkboxMarkedOutline" />
            </div>
            <div class="SectionGHGDemo__badge-text">SSO/SCIM</div>
          </div>
        </div>
        <!-- Right overlay metric card -->
        <div
          :class="[
            'SectionGHGDemo__metric',
            { 'SectionGHGDemo__cards--hidden': isPlaying },
          ]"
        >
          <MetricCard
            title="CO₂ Emissions"
            subtitle="Within a year"
            trendText="↓ 40%"
            trendTheme="success"
            width="18.5rem"
          />
        </div>
      </div>
    </UIContainer>
  </div>
</template>

<script setup lang="ts">
import SectionTitle from '@/views/components/misc/SectionTitle.vue'
import { MetricCard } from '@/views/components/sections/components/MetricCard'
import UIContainer from '@/views/components/ui/containers/UIContainer/UIContainer.vue'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'
import { onMounted, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    /** YouTube video id, e.g. "dQw4w9WgXcQ" */
    videoId?: string
  }>(),
  {
    videoId: 'MStYRYOR0ns',
  },
)

const playerEl = ref<HTMLDivElement | null>(null)
const player = ref<any>(null)
const isPlaying = ref(false)

declare global {
  interface Window {
    YT: any
    onYouTubeIframeAPIReady: () => void
  }
}

function loadYouTubeApi(): Promise<void> {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve()
      return
    }
    const existing = document.getElementById(
      'youtube-iframe-api',
    ) as HTMLScriptElement | null
    if (existing) {
      // API script already present, wait for it to be ready
      const check = () => {
        if (window.YT && window.YT.Player) resolve()
        else setTimeout(check, 50)
      }
      check()
      return
    }
    const tag = document.createElement('script')
    tag.src = 'https://www.youtube.com/iframe_api'
    tag.id = 'youtube-iframe-api'
    document.body.appendChild(tag)
    window.onYouTubeIframeAPIReady = () => resolve()
  })
}

function initPlayer() {
  if (!playerEl.value) return
  player.value = new window.YT.Player(playerEl.value, {
    width: '100%',
    height: '100%',
    videoId: props.videoId,
    playerVars: {
      modestbranding: 1,
      rel: 0,
      controls: 1,
      playsinline: 1,
    },
    events: {
      onStateChange: onPlayerStateChange,
    },
  })
}

function onPlayerStateChange(event: any) {
  const state = event?.data
  const YTState = window.YT?.PlayerState
  if (!YTState) return
  if (state === YTState.PLAYING) {
    isPlaying.value = true
  } else if (state === YTState.PAUSED || state === YTState.ENDED) {
    isPlaying.value = false
  }
}

onMounted(async () => {
  await loadYouTubeApi()
  initPlayer()
})
</script>

<style lang="scss" scoped>
#SectionGHGDemo {
  position: relative;
  background-color: $color-ui-gray-cool-50;
  .SectionGHGDemo__image-container {
    display: flex;
  }

  .SectionGHGDemo__title-container {
    display: flex;
    justify-content: center;
    padding-bottom: 3rem;
  }

  .SectionTitle {
    max-width: 46rem;
    text-align: center;
  }

  .SectionGHGDemo__video-container {
    display: flex;
    justify-content: center;
    padding: 0rem 3rem;
    position: relative;
  }

  .SectionGHGDemo__video {
    position: relative;
    width: 100%;
    max-width: 64rem;
    aspect-ratio: 16/9;
    border-radius: 0.75rem;
    overflow: hidden;
    background: radial-gradient(120% 120% at 50% 0%, #0a3a33 0%, #05231e 100%);
  }

  .SectionGHGDemo__player {
    position: absolute;
    inset: 0;
  }

  /* Ensure injected iframe expands fully */
  .SectionGHGDemo__player :deep(iframe) {
    display: block;
    width: 100% !important;
    height: 100% !important;
  }

  .SectionGHGDemo__left-cards {
    position: absolute;
    top: 2rem;
    left: 1rem;
    display: none;
    gap: 0.75rem;
    z-index: 2;
    transition:
      transform 260ms ease,
      opacity 260ms ease;
    @include md {
      display: grid;
    }
  }

  .SectionGHGDemo__badge {
    background: rgba(255, 255, 255, 0.9);
    opacity: 0.95;
    color: $color-ui-gray-cool-800;
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;
    @include font-size-14px();
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    backdrop-filter: blur(8px);
  }

  .SectionGHGDemo__badge-icon {
    --size: 1.25rem;
    height: 2rem;
    width: 2rem;
    border-radius: 9999px;
    background: rgba(3, 121, 93, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;

    :deep(.UIIcon) {
      --size: 1rem;
      --color: #035f4e; // dark teal to match theme
    }
  }

  .SectionGHGDemo__badge-text {
    line-height: 1.2;
  }

  .SectionGHGDemo__metric {
    position: absolute;
    right: 1rem;
    top: 7.5rem;
    z-index: 2;
    transition:
      transform 260ms ease,
      opacity 260ms ease;
    display: none;
    @include md {
      display: block;
    }
  }

  .SectionGHGDemo__metric-title {
    @include font-size-30px();
    font-weight: 600;
    color: $color-ui-gray-cool-900;
    margin-bottom: 0.25rem;
  }

  .SectionGHGDemo__metric-subtitle {
    @include font-size-16px();
    color: $color-ui-gray-cool-600;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .SectionGHGDemo__metric-trend {
    @include font-size-14px();
    color: $color-ui-success-main;
    font-weight: 600;
  }

  .SectionGHGDemo__cards--hidden {
    opacity: 0;
    pointer-events: none;
    transform: translateX(-1rem);
  }

  .SectionGHGDemo__metric.SectionGHGDemo__cards--hidden {
    transform: translateX(1rem);
  }
}
</style>
